/**
 * Phase 27.53.H — Reply From Hub compose dialog.
 *
 * Used by both tickets and contract-request notes panels. When
 * launched from a specific email-in note, we pre-fill `to`, `subject`
 * (as ``Re: …``), and the visibility toggle. The caller supplies a
 * ``sendReply({to, subject, content, visibility})`` function that
 * dispatches to the right backend endpoint.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';

export default function ReplyComposeDialog({
  open,
  onOpenChange,
  seedNote,        // optional {from_email, subject}
  sendReply,       // async ({to, subject, content, visibility}) => note
  onSent,          // async () => void — parent refreshes list
  testidPrefix = 'reply',
}) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(seedNote?.from_email || seedNote?.to_email || '');
    const s = seedNote?.subject || '';
    setSubject(s ? (s.toLowerCase().startsWith('re:') ? s : `Re: ${s}`) : '');
    setContent('');
    setInternal(false);
  }, [open, seedNote]);

  const handleSend = async () => {
    if (!to.trim() || !subject.trim() || !content.trim()) {
      toast.error('To, Subject and Message are required'); return;
    }
    setSending(true);
    try {
      await sendReply({
        to: to.trim(),
        subject: subject.trim(),
        content,
        visibility: internal ? 'internal' : 'visible',
      });
      toast.success('Reply sent + logged');
      onOpenChange(false);
      await onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Reply from Hub</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="alice@example.com"
              data-testid={`${testidPrefix}-to`}
            />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Re: …"
              data-testid={`${testidPrefix}-subject`}
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              placeholder="Type your reply…"
              className="resize-y min-h-[140px]"
              data-testid={`${testidPrefix}-body`}
            />
            <p className="text-[10px] text-slate-500 mt-1">
              A copy is logged as an outgoing note. Recipient replies land back here automatically.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
              className="rounded border-slate-300"
              data-testid={`${testidPrefix}-internal-toggle`}
            />
            🔒 Log the outgoing note as internal (agent won't see it in the Hub)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid={`${testidPrefix}-send`}>
            <Send size={14} className="mr-1.5" />
            {sending ? 'Sending…' : 'Send Reply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
